import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import { Switch } from 'src/components/Switch';
import { TriSwitch } from 'src/components/TriSwitch';
// import { IconSwitch } from 'src/components'; // kept with the hidden community Mems mini-toggle below
import { TrackingApi } from 'src/api';
import { LearningToggleFeatureId } from 'src/api/settings/learningToggles';
import { LearningSessionCustomiseToggleState } from 'src/api/tracking/types';
import { sideDrawerBackdropMotion, sideDrawerSlideMotion } from 'src/components/SideDrawer/motion';
import {
  useCurrentLanguagePair,
  useKeyboardShortcut,
  useLearningToggleSettings,
  useShowRomanizationSetting,
} from 'src/hooks';
import { useUserContext } from 'src/context/user';
import NotificationContext from 'src/context/notification';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import {
  TracingDifficulty,
  getStoredTracingDifficulty,
  setStoredTracingDifficulty,
} from 'src/utils/tracingDifficulty';
import { isProduction } from 'src/utils/env/isProduction';
import {
  isArabicScriptLanguageId,
  isCharacterTracingLanguageId,
  isJapaneseLanguageId,
} from 'src/utils/language/targetLanguageIds';
import { isEnglishLanguage } from 'src/utils/language/isEnglishLanguage';
import { NuggetToggleMode } from 'src/utils/nuggetExpanded';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'learning_session_tooltip_customise',
    defaultMessage: 'Customise learning experience',
  },
  close: { id: 'close' },
  memsLabel: { id: 'mem_gallery_title' },
  memsDescription: {
    id: 'learning_session_customise_mems_description',
    defaultMessage: 'Funny memory aids written by the community, to aid recall',
  },
  nuggetsLabel: { id: 'learn_presentationcard_nugget_title' },
  nuggetsDescription: {
    id: 'learning_session_customise_nuggets_description',
    defaultMessage: 'Extra information regarding usage, etymology, cultural pitfalls, etc.',
  },
  nuggetsOnHint: {
    id: 'learning_session_customise_nuggets_on_hint',
    defaultMessage: 'Cycle again to expand every nugget automatically.',
  },
  nuggetsExpandedHint: {
    id: 'learning_session_customise_nuggets_expanded_hint',
    defaultMessage: 'Every nugget stays fully open.',
  },
  showRomanizationLabel: {
    id: 'learning_session_customise_show_romanization_label',
    defaultMessage: 'Show romanization',
  },
  showRomanizationDescription: {
    id: 'learning_session_customise_show_romanization_description',
    defaultMessage: 'Display the Latin letters to help pronouncing words in the native script',
  },
  useRomanizationLabel: { id: 'settings_learningSettings_title_romanization' },
  useRomanizationDescription: { id: 'settings_learningSettings_description_romanization' },
  useRomanizationNextSession: {
    id: 'learning_session_customise_use_romanization_next_session',
    defaultMessage: 'Applies from your next session',
  },
  literalTranslationLabel: { id: 'literal_translation' },
  literalTranslationDescription: {
    id: 'learning_session_customise_literal_translation_description',
    defaultMessage:
      'Seeing phrases written down word-for-word improves your understanding of grammar',
  },
  sampleSentencesLabel: {
    id: 'learning_session_customise_sample_sentences_label',
    defaultMessage: 'Example Sentences',
  },
  sampleSentencesDescription: {
    id: 'learning_session_customise_sample_sentences_description',
    defaultMessage: "Frame what you're learning in context to clarify usage",
  },
  pitchAccentLabel: {
    id: 'learning_session_customise_pitch_accent_label',
    defaultMessage: 'Pitch accent',
  },
  pitchAccentDescription: {
    id: 'learning_session_customise_pitch_accent_description',
    defaultMessage: 'See how Japanese words rise and fall in pitch',
  },
  showAnswerBeforeTestLabel: {
    id: 'learningsettings_difficultwords_show_answer_before_test',
    defaultMessage: 'Show answer before test',
  },
  showAnswerBeforeTestDescription: {
    id: 'learningsettings_difficultwords_show_answer_before_test_desc',
    defaultMessage: 'See the answer before difficult words tests. Turn off for more challenge.',
  },
  arabicBreakdownLabel: {
    id: 'learning_session_customise_arabic_breakdown_label',
    defaultMessage: 'Letter breakdown',
  },
  arabicBreakdownDescription: {
    id: 'learning_session_customise_arabic_breakdown_description',
    defaultMessage: 'See how a word splits into its individual letters',
  },
  phoneticSpellingLabel: {
    id: 'learning_session_customise_phonetic_spelling_label',
    defaultMessage: 'Phonetic Spelling',
  },
  phoneticSpellingDescription: {
    id: 'learning_session_customise_phonetic_spelling_description',
    defaultMessage:
      'Provide pronunciation guides to aid speaking, with stressed syllables in CAPS.',
  },
  seeMoreInSettings: {
    id: 'learning_session_customise_see_more_settings',
    defaultMessage: 'See more in learning settings',
  },
  tracingLabel: {
    id: 'learning_session_customise_tracing_label',
    defaultMessage: 'Trace characters',
  },
  tracingDescription: {
    id: 'learning_session_customise_tracing_description',
    defaultMessage: 'Practice writing characters by tracing them on screen',
  },
  tracingDifficultyEasy: {
    id: 'learning_session_customise_tracing_difficulty_easy',
    defaultMessage: 'Easy',
  },
  tracingDifficultyEasyDescription: {
    id: 'learning_session_customise_tracing_difficulty_easy_description',
    defaultMessage: 'Full outline shown while tracing',
  },
  tracingDifficultyMedium: {
    id: 'learning_session_customise_tracing_difficulty_medium',
    defaultMessage: 'Medium',
  },
  tracingDifficultyMediumDescription: {
    id: 'learning_session_customise_tracing_difficulty_medium_description',
    defaultMessage: 'Outline fades after a moment',
  },
  tracingDifficultyHard: {
    id: 'learning_session_customise_tracing_difficulty_hard',
    defaultMessage: 'Hard',
  },
  tracingDifficultyHardDescription: {
    id: 'learning_session_customise_tracing_difficulty_hard_description',
    defaultMessage: 'No outline at all',
  },
  // Hidden for now — keep so the community Mems mini-toggle can be restored.
  // communityMemsToggle: {
  //   id: 'learning_session_customise_community_mems_toggle',
  //   defaultMessage: 'Show community Mems',
  // },
});

interface CustomiseLearningFeaturesModalProps {
  isOpen: boolean;
  onClose: () => void;
  wordlistId?: string;
}

interface FeatureToggle {
  id: LearningToggleFeatureId;
  labelMessage: MessageDescriptor;
  descriptionMessage?: MessageDescriptor;
}

interface PortalProps {
  children: React.ReactNode;
}

const Portal = ({ children }: PortalProps) => {
  const domNode = document.querySelector('#modalPortalRoot');
  /* istanbul ignore next */
  if (!domNode) {
    throw new Error('Could not find modalPortalRoot');
  }

  return createPortal(children, domNode);
};

function useFocus<Element extends HTMLElement>(
  ref: React.RefObject<Element | null>,
  shouldFocus: boolean,
) {
  useEffect(() => {
    if (shouldFocus && ref && ref.current) {
      ref.current.focus();
    }
  }, [shouldFocus, ref]);
}

export const CustomiseLearningFeaturesModal: FC<CustomiseLearningFeaturesModalProps> = ({
  isOpen,
  onClose,
  wordlistId,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const { setNotification } = useContext(NotificationContext);
  const { isFeatureShown, isLoading, nuggetMode, setFeatureShown, setNuggetMode, shownState } =
    useLearningToggleSettings();
  const { languagePair } = useCurrentLanguagePair();
  const {
    showRomanization: teachWithRomanization,
    isLoading: isTeachRomanizationLoading,
    setShowRomanization: setTeachWithRomanization,
  } = useShowRomanizationSetting(languagePair?.languagePairId);
  const targetLanguageId = languagePair?.targetLanguageId;
  const showRomanizationToggle = !!languagePair?.isScript;
  const showTeachRomanizationToggle =
    showRomanizationToggle &&
    (isTeachRomanizationLoading || typeof teachWithRomanization === 'boolean');
  const showPitchAccentToggle = isJapaneseLanguageId(targetLanguageId);
  const showArabicBreakdownToggle = isArabicScriptLanguageId(targetLanguageId);
  const showPhoneticSpellingToggle = isEnglishLanguage(languagePair?.sourceLanguageLocale);
  const showTracingSection = !isProduction() && isCharacterTracingLanguageId(targetLanguageId);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const [tracingDifficulty, setTracingDifficulty] = useState<TracingDifficulty>(
    getStoredTracingDifficulty,
  );
  useFocus(drawerContentRef, isOpen);

  /* istanbul ignore next: default function */
  useKeyboardShortcut('esc', isOpen ? onClose : () => null);

  const [hasOpened, setHasOpened] = useState(false);
  const titleText = intl.formatMessage(msgs.title);

  useEffect(() => {
    if (isOpen && !hasOpened) {
      setHasOpened(true);
    }
  }, [isOpen, hasOpened]);

  const features: FeatureToggle[] = [
    {
      id: 'mems',
      labelMessage: msgs.memsLabel,
      descriptionMessage: msgs.memsDescription,
    },
    {
      id: 'nuggets',
      labelMessage: msgs.nuggetsLabel,
      descriptionMessage: msgs.nuggetsDescription,
    },
    ...(showRomanizationToggle
      ? [
          {
            id: 'romanization' as const,
            labelMessage: msgs.showRomanizationLabel,
            descriptionMessage: msgs.showRomanizationDescription,
          },
        ]
      : []),
    {
      id: 'literalTranslation',
      labelMessage: msgs.literalTranslationLabel,
      descriptionMessage: msgs.literalTranslationDescription,
    },
    {
      id: 'sampleSentences',
      labelMessage: msgs.sampleSentencesLabel,
      descriptionMessage: msgs.sampleSentencesDescription,
    },
    ...(showPitchAccentToggle
      ? [
          {
            id: 'pitchAccent' as const,
            labelMessage: msgs.pitchAccentLabel,
            descriptionMessage: msgs.pitchAccentDescription,
          },
        ]
      : []),
    {
      id: 'showAnswerBeforeTest',
      labelMessage: msgs.showAnswerBeforeTestLabel,
      descriptionMessage: msgs.showAnswerBeforeTestDescription,
    },
    ...(showPhoneticSpellingToggle
      ? [
          {
            id: 'phoneticSpelling' as const,
            labelMessage: msgs.phoneticSpellingLabel,
            descriptionMessage: msgs.phoneticSpellingDescription,
          },
        ]
      : []),
    ...(showArabicBreakdownToggle
      ? [
          {
            id: 'arabicBreakdown' as const,
            labelMessage: msgs.arabicBreakdownLabel,
            descriptionMessage: msgs.arabicBreakdownDescription,
          },
        ]
      : []),
  ];

  const trackToggleFlipped = (field: string, state: LearningSessionCustomiseToggleState) => {
    void TrackingApi.trackUserAction(
      'toggle_flipped',
      'learning_session_customise_learning_experience',
      getLanguagePairId(),
      !!user?.isPro,
      {
        field,
        state,
        ...(wordlistId && { wordlist_id: wordlistId }),
      },
    );
  };

  const handleToggleChange = (featureId: LearningToggleFeatureId, shown: boolean) => {
    setFeatureShown(featureId, shown);
    trackToggleFlipped(featureId, shown ? 'on' : 'off');
  };

  const handleNuggetModeChange = (mode: NuggetToggleMode) => {
    setNuggetMode(mode);
    trackToggleFlipped('nuggets', mode);
  };

  const handleTeachRomanizationChange = (shown: boolean) => {
    setTeachWithRomanization(shown);
    trackToggleFlipped('show_romanization', shown ? 'on' : 'off');
    setNotification({
      text: intl.formatMessage(msgs.useRomanizationNextSession),
      status: 'info',
      snackBar: true,
    });
  };

  // Hidden for now — keep so the community Mems mini-toggle can be restored.
  // const handleCommunityMemsChange = (shown: boolean) => {
  //   setFeatureShown('communityMems', shown);
  //   trackToggleFlipped('communityMems', shown ? 'on' : 'off');
  // };

  const togglesReady = !isLoading && shownState !== null;
  const teachRomanizationReady = !isTeachRomanizationLoading && showTeachRomanizationToggle;
  const tracingEnabled = isFeatureShown('characterTracing');

  if (!isOpen && !hasOpened) return null;

  return (
    <Portal>
      <div role="dialog" aria-label={titleText} aria-modal="true">
        <AnimatePresence>
          {isOpen && (
            <>
              <S.Backdrop
                key="customise-learning-drawer-backdrop"
                data-testid="customiseLearningFeaturesDrawerBackdrop"
                onClick={onClose}
                {...sideDrawerBackdropMotion}
              />
              <S.Root
                key="customise-learning-drawer"
                data-testid="customiseLearningFeaturesDrawer"
                data-language-pair-id={languagePair?.languagePairId ?? ''}
                ref={drawerContentRef}
                tabIndex={-1}
                {...sideDrawerSlideMotion}
              >
                <S.CloseButton
                  onClick={onClose}
                  onKeyUp={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onClose();
                    }
                  }}
                  data-testid="customiseLearningFeaturesDrawerCloseBtn"
                  aria-label={intl.formatMessage(msgs.close)}
                  tabIndex={0}
                >
                  <S.CloseIcon name="close" />
                </S.CloseButton>
                <S.Title>{titleText}</S.Title>

                <S.FeaturesContainer>
                  {features.map(feature => (
                    <Fragment key={feature.id}>
                      <S.FeatureRow>
                        <S.FeatureInfo>
                          <S.FeatureLabel>
                            {intl.formatMessage(feature.labelMessage)}
                          </S.FeatureLabel>
                          {feature.descriptionMessage && (
                            <S.FeatureDescription>
                              {intl.formatMessage(feature.descriptionMessage)}
                            </S.FeatureDescription>
                          )}
                          {feature.id === 'nuggets' && nuggetMode === 'on' && (
                            <S.FeatureDescription>
                              {intl.formatMessage(msgs.nuggetsOnHint)}
                            </S.FeatureDescription>
                          )}
                          {feature.id === 'nuggets' && nuggetMode === 'expanded' && (
                            <S.FeatureDescription>
                              {intl.formatMessage(msgs.nuggetsExpandedHint)}
                            </S.FeatureDescription>
                          )}
                          {/* Hidden for now — keep so the community Mems mini-toggle can be restored.
                          {feature.id === 'mems' && isFeatureShown('mems') && (
                            <IconSwitch
                              checked={isFeatureShown('communityMems')}
                              onChange={handleCommunityMemsChange}
                              onIconName="community"
                              offIconName="close"
                              ariaLabel={intl.formatMessage(msgs.communityMemsToggle)}
                              testId="toggle-community-mems"
                            />
                          )}
                          */}
                        </S.FeatureInfo>
                        {feature.id === 'nuggets' ? (
                          <TriSwitch
                            value={nuggetMode}
                            onChange={mode => {
                              if (!togglesReady) {
                                return;
                              }

                              handleNuggetModeChange(mode);
                            }}
                            disabled={!togglesReady}
                            aria-label={intl.formatMessage(feature.labelMessage)}
                            data-testid="toggle-nuggets"
                          />
                        ) : (
                          <Switch
                            checked={isFeatureShown(feature.id)}
                            onChange={event => {
                              if (!togglesReady) {
                                return;
                              }

                              const shown =
                                event?.target && 'checked' in event.target
                                  ? event.target.checked
                                  : !isFeatureShown(feature.id);

                              handleToggleChange(feature.id, shown);
                            }}
                            disabled={!togglesReady}
                            data-testid={`toggle-${feature.id}`}
                          />
                        )}
                      </S.FeatureRow>
                      {feature.id === 'romanization' && showTeachRomanizationToggle && (
                        <S.FeatureRow>
                          <S.FeatureInfo>
                            <S.FeatureLabel>
                              {intl.formatMessage(msgs.useRomanizationLabel)}
                            </S.FeatureLabel>
                            <S.FeatureDescription>
                              {intl.formatMessage(msgs.useRomanizationDescription)}
                            </S.FeatureDescription>
                            <S.FeatureDescription>
                              {intl.formatMessage(msgs.useRomanizationNextSession)}
                            </S.FeatureDescription>
                          </S.FeatureInfo>
                          <Switch
                            checked={!!teachWithRomanization}
                            onChange={event => {
                              if (!teachRomanizationReady) {
                                return;
                              }

                              const shown =
                                event?.target && 'checked' in event.target
                                  ? event.target.checked
                                  : !teachWithRomanization;

                              handleTeachRomanizationChange(shown);
                            }}
                            disabled={!teachRomanizationReady}
                            data-testid="toggle-show-romanization"
                          />
                        </S.FeatureRow>
                      )}
                    </Fragment>
                  ))}
                </S.FeaturesContainer>

                {showTracingSection && (
                  <S.TracingSection>
                    <S.FeatureRow>
                      <S.FeatureInfo>
                        <S.FeatureLabel>{intl.formatMessage(msgs.tracingLabel)}</S.FeatureLabel>
                        <S.FeatureDescription>
                          {intl.formatMessage(msgs.tracingDescription)}
                        </S.FeatureDescription>
                      </S.FeatureInfo>
                      <Switch
                        checked={tracingEnabled}
                        onChange={event => {
                          if (!togglesReady) {
                            return;
                          }

                          const enabled =
                            event?.target && 'checked' in event.target
                              ? event.target.checked
                              : !tracingEnabled;

                          handleToggleChange('characterTracing', enabled);
                        }}
                        disabled={!togglesReady}
                        data-testid="toggle-tracing"
                      />
                    </S.FeatureRow>
                    {tracingEnabled && (
                      <S.DifficultyOptions data-testid="tracing-difficulty-options">
                        <S.DifficultyOption>
                          <S.DifficultyRadio
                            type="radio"
                            name="tracingDifficulty"
                            value="easy"
                            checked={tracingDifficulty === 'easy'}
                            onChange={() => {
                              setTracingDifficulty('easy');
                              setStoredTracingDifficulty('easy');
                            }}
                            data-testid="tracing-difficulty-easy"
                          />
                          <div>
                            <S.DifficultyLabel>
                              {intl.formatMessage(msgs.tracingDifficultyEasy)}
                            </S.DifficultyLabel>
                            <S.DifficultyDescription>
                              {intl.formatMessage(msgs.tracingDifficultyEasyDescription)}
                            </S.DifficultyDescription>
                          </div>
                        </S.DifficultyOption>
                        <S.DifficultyOption>
                          <S.DifficultyRadio
                            type="radio"
                            name="tracingDifficulty"
                            value="medium"
                            checked={tracingDifficulty === 'medium'}
                            onChange={() => {
                              setTracingDifficulty('medium');
                              setStoredTracingDifficulty('medium');
                            }}
                            data-testid="tracing-difficulty-medium"
                          />
                          <div>
                            <S.DifficultyLabel>
                              {intl.formatMessage(msgs.tracingDifficultyMedium)}
                            </S.DifficultyLabel>
                            <S.DifficultyDescription>
                              {intl.formatMessage(msgs.tracingDifficultyMediumDescription)}
                            </S.DifficultyDescription>
                          </div>
                        </S.DifficultyOption>
                        <S.DifficultyOption>
                          <S.DifficultyRadio
                            type="radio"
                            name="tracingDifficulty"
                            value="hard"
                            checked={tracingDifficulty === 'hard'}
                            onChange={() => {
                              setTracingDifficulty('hard');
                              setStoredTracingDifficulty('hard');
                            }}
                            data-testid="tracing-difficulty-hard"
                          />
                          <div>
                            <S.DifficultyLabel>
                              {intl.formatMessage(msgs.tracingDifficultyHard)}
                            </S.DifficultyLabel>
                            <S.DifficultyDescription>
                              {intl.formatMessage(msgs.tracingDifficultyHardDescription)}
                            </S.DifficultyDescription>
                          </div>
                        </S.DifficultyOption>
                      </S.DifficultyOptions>
                    )}
                  </S.TracingSection>
                )}

                <S.SettingsLink href="/learning-settings" target="_blank" rel="noopener">
                  {intl.formatMessage(msgs.seeMoreInSettings)}
                </S.SettingsLink>
              </S.Root>
            </>
          )}
        </AnimatePresence>
      </div>
    </Portal>
  );
};
